// ─── Session types shared between SessionList and SessionView ─────────

/** Session lifecycle state returned by the backend.
 *
 * Stable states: off, on, ongoing
 * Transient states: starting, stopping, connecting, destroying
 */
export type SessionState =
  | "off"
  | "starting"
  | "on"
  | "connecting"
  | "ongoing"
  | "stopping"
  | "destroying";

/** Summary returned by GET /api/v1/sessions (list endpoint). */
export interface SessionSummary {
  session_id: string;
  state: SessionState;
  created_at: string;
  browser?: string;
  name?: string;
  last_connected_at?: string;
  connected_from?: string;
  thumbnail?: string;
  context_id?: string;
  context_mode: "persistent" | "legacy";
  context_label: string;
  profile_actions_allowed: boolean;
  context_base_revision?: number;
  profile_sync_status?: "pending" | "syncing" | "synced" | "error" | "failed";
  profile_sync_error?: string;
  retention?: "hot" | "frozen" | "cooling" | "cold";
  lifecycle_generation?: number;
  lifecycle_checkpoint_status?:
    | "pending"
    | "syncing"
    | "synced"
    | "noop"
    | "conflict"
    | "error"
    | "failed";
  lifecycle_candidate_id?: string;
  destroy_allowed?: boolean;
}

/** Detail returned by GET /api/v1/sessions/:id. */
export interface SessionDetail extends SessionSummary {
  started_at?: string;
  ready_info?: {
    webrtc_signaling: string;
    display_resolution: string;
    codec: string;
    framerate: number;
    bitrate: number;
  };
}

/** Display state for session cards (F3). */
export type DisplayState = "ongoing" | "on" | "off" | "transitioning";

/** Map backend state to display state. */
export function getDisplayState(state: SessionState): DisplayState {
  switch (state) {
    case "ongoing":
      return "ongoing";
    case "on":
      return "on";
    case "off":
      return "off";
    default:
      return "transitioning";
  }
}

/** Is this state transient (in-progress action)? */
export function isTransientState(state: SessionState): boolean {
  return state === "starting" || state === "stopping" || state === "connecting" || state === "destroying";
}

/** Response from GET /api/v1/sessions. */
export interface SessionsListResponse {
  sessions: SessionSummary[];
  count: number;
}

/** Response from POST /api/v1/sessions. */
export interface CreateSessionResponse {
  session_id: string;
  session_token: string;
  state: "starting";
}

// ─── Type guards ──────────────────────────────────────────────────────

export function isSessionSummary(value: unknown): value is SessionSummary {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  if ("owner_id" in obj || "session_token" in obj) return false;
  const validStates: SessionState[] = [
    "off",
    "starting",
    "on",
    "connecting",
    "ongoing",
    "stopping",
    "destroying",
  ];
  return (
    typeof obj.session_id === "string" &&
    validStates.some((state) => state === obj.state) &&
    typeof obj.created_at === "string" &&
    (obj.browser === undefined || typeof obj.browser === "string") &&
    (obj.name === undefined || typeof obj.name === "string") &&
    (obj.context_mode === "persistent" || obj.context_mode === "legacy") &&
    typeof obj.context_label === "string" &&
    typeof obj.profile_actions_allowed === "boolean" &&
    ((obj.context_mode === "persistent" &&
      typeof obj.context_id === "string" &&
      obj.profile_actions_allowed) ||
      (obj.context_mode === "legacy" &&
        obj.context_id === undefined &&
        !obj.profile_actions_allowed)) &&
    (obj.context_base_revision === undefined ||
      (typeof obj.context_base_revision === "number" &&
        Number.isInteger(obj.context_base_revision) &&
        obj.context_base_revision >= 0)) &&
    (obj.destroy_allowed === undefined || typeof obj.destroy_allowed === "boolean")
  );
}

export function isSessionDetail(value: unknown): value is SessionDetail {
  return isSessionSummary(value);
}

export function isSessionsListResponse(
  value: unknown,
): value is SessionsListResponse {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    Array.isArray(obj.sessions) &&
    obj.sessions.every(isSessionSummary) &&
    typeof obj.count === "number" &&
    Number.isInteger(obj.count) &&
    obj.count >= obj.sessions.length
  );
}

export function isCreateSessionResponse(
  value: unknown,
): value is CreateSessionResponse {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.session_id === "string" &&
    typeof obj.session_token === "string" &&
    obj.state === "starting"
  );
}
